import {Component, Input, signal, Signal} from '@angular/core';
import {
  File,
  Folder,
  Mutation,
  Organization,
  PreviewTokenPayload,
  ProcessStateEnum,
  Query,
  Tag,
  TagInput,
  TrackType
} from '../../../types-repository';
import {ActivatedRoute, Router, RouterLink} from '@angular/router';
import {BehaviorSubject, combineLatest, EMPTY, forkJoin, Subscription} from 'rxjs';
import {errorText} from '../../../graphql/error-link';
import {throwOnError} from '../../../graphql/gql-load';
import {catchError, filter, map, mergeMap, skipWhile, switchMap, take, tap} from 'rxjs/operators';
import {GET_FOLDER_ALL_DETAILS, GET_UPFRONT_TOKEN, PREVIEW_GENERATE} from '../../../queries-repository';
import {PreviewRegistrationService} from '../../../services/preview-registration.service';
import {Apollo} from 'apollo-angular';
import {BannerService} from '../../../banner/banner.service';
import gql from 'graphql-tag';
import {Constants} from '../../../globals';
import {Dialog} from '@angular/cdk/dialog';
import {GenerateUploadLinkComponent} from '../generate-upload-link-dialog/generate-upload-link-dialog.component';
import {PoMetaService} from '../../../services/po-meta.service';
import {ProcessService} from '../../../services/process.service';
import {ProcessButtonService} from '../../../services/process-button.service';
import {ProcessButtonDirective} from '../../../process-button.directive';
import {DoButtonComponent} from '../../do-button/do-button.component';
import {S3LinkComponent} from '../../s3-link/s3-link.component';
import {IsAllowedPipe} from '../../../pipes/has-action-pipe';
import {MezzPreviewPlayerComponent} from './mezz-preview-player.component';
import {FileFolderLinkPipe} from '../../file-folder-link.pipe';
import {FormsModule} from '@angular/forms';
import {ContextService} from '../../../services/context.service';
import {CommonModule} from '@angular/common';
import {FormFooterComponent} from '../../form-footer/form-footer.component';
import {RemovePipe} from '../../../pipes/remove-pipe';
import {TagDialogComponent, TagDialogData, TagDialogResult} from './tag-dialog.component';
import {duplicateTagKeys} from './folder-tags';

export interface FileTrack {
  file: File;
  track: {
    index: number;
    enabled: boolean;
    language: string;
    track_type: TrackType;
  }
  channelLayout?: string;
  channelWarn?: boolean;
  criticalQc?: boolean;
  warningQc?: boolean;
}

@Component({
  selector: 'app-folder-asset-view',
  templateUrl: './folder-asset-view.component.html',
  styleUrls: ['./folder-asset-view.component.scss'],
  imports: [
    FormFooterComponent,
    S3LinkComponent,
    IsAllowedPipe,
    MezzPreviewPlayerComponent,
    FileFolderLinkPipe,
    RouterLink,
    FormsModule,
    CommonModule,
    RemovePipe,
    ProcessButtonDirective,
  ]
})
export class FolderAssetViewComponent {

  folderSubject = new BehaviorSubject<Folder>(null);

  organization: Signal<Organization>;
  loading = signal(true);
  /** Why the folder's details could not be loaded. */
  loadError = signal<string | null>(null);

  _folder: Folder;
  parent: Folder

  videoTracks: FileTrack[] = [];
  audioTracks: FileTrack[] = [];
  textTracks: FileTrack[] = [];
  imageTracks: FileTrack[] = [];

  languages = Constants.languages;

  selectedVideoFileTrack: FileTrack;
  selectedAudioFileTrack: FileTrack;
  selectedTextFileTrack: FileTrack;

  previewTokenPayload: PreviewTokenPayload;
  tags: Tag[];

  public readonly TrackType = TrackType;

  subscriptions: Subscription[] = [];

  private readonly CRITICAL_QC_CODES = new Set([
    'SUBS_MISSING_FONT',
    'SUBS_MISSING_GLYPHS',
    'SUBS_EMPTY_SOURCE',
    'SUBS_CANNOT_READ_SOURCE',
    'SUBS_UNSUPPORTED_FORMAT',
  ]);

  private qcFlags(file: File): { criticalQc: boolean; warningQc: boolean } {
    let criticalQc = false;
    let warningQc = false;
    for (const w of file.warnings ?? []) {
      try {
        const parsed = JSON.parse(w);
        if (parsed?.level === 'error' || (!parsed?.level && this.CRITICAL_QC_CODES.has(parsed?.code))) {
          criticalQc = true;
        } else if (parsed?.level === 'warning' || (!parsed?.level && !this.CRITICAL_QC_CODES.has(parsed?.code))) {
          warningQc = true;
        }
      } catch { /* ignore malformed entries */ }
      if (criticalQc && warningQc) break;
    }
    return { criticalQc, warningQc };
  }

  @Input()
  set folder(folder: Folder) {
    this.folderSubject.next(folder);
    this._folder = folder;
  }


  constructor(
    private router: Router,
    private route: ActivatedRoute,
    private previewRegistrationService: PreviewRegistrationService,
    private apollo: Apollo,
    private banner: BannerService,
    private contextService: ContextService,
    public poMetaService: PoMetaService,
    private dialog: Dialog,
    private processService: ProcessService,
    private pb: ProcessButtonService,
  ) {
    this.organization = this.contextService.organization

    this.folderSubject.pipe(
      filter(folder => !!folder),
      tap(() => this.loadError.set(null)),
      switchMap(folder => this.apollo.watchQuery<Query>({
          query: GET_FOLDER_ALL_DETAILS,
          variables: {
            id: folder.id
          },
          nextFetchPolicy: 'cache-only',
        }
      ).valueChanges.pipe(
        throwOnError(),
        filter(response => !!response.data),
        map(response => response.data.folder as Folder)).pipe(
        tap(() => this.loading.set(false)),
        // A failed load says why; the next folder loads again (the error ends only this watch).
        catchError(e => {
          this.loadError.set(errorText(e));
          return EMPTY;
        }),
      ))).subscribe(_folder => {
      if (_folder.parents) {
        let ps = _folder.parents
        ps = [...ps].reverse()
        while (ps[0].id === this.folderSubject.value.id) {
          ps.shift()
        }
        this.parent = ps.shift()
      }

      this._folder = _folder;
      this.tags = this._folder.tags;
      if (this.tags == null || this.tags.length === 0) {
        this.tags = []
      }
      this.tags = this.tags.map(t => {
        return {'key': t.key, 'value': t.value}
      });
      this.audioTracks = [];
      this.videoTracks = [];
      this.textTracks = [];
      this.imageTracks = [];
      for (const file of _folder.files) {
        if (file.tracks && !file.deleted) {
          const qc = this.qcFlags(file);
          for (const track of file.tracks) {
            if (track.track_type === TrackType.Video) {
              this.videoTracks.push({file, track: {...track}, ...qc})
            } else if (track.track_type === TrackType.Audio) {
              let channelLayout = '';
              let channelWarn = false;
              if (file.mediainfo_json) {
                const trackJson = file.mediainfo_json
                  .map(s => JSON.parse(s))
                  .find(s => (s.hasOwnProperty('StreamOrder') ? parseInt(s['StreamOrder'], 10) : 0) === track.index)
                if (trackJson) {
                  channelLayout = trackJson.hasOwnProperty('ChannelLayout') ? trackJson['ChannelLayout'] :
                    (trackJson.hasOwnProperty('Channels') ? trackJson['Channels'] + ' Ch' : 'unknown')
                  channelWarn = trackJson.hasOwnProperty('Channels') && trackJson['Channels'] !== '2'
                }
              }
              this.audioTracks.push({file, track: {...track}, channelLayout, channelWarn, ...qc})
            } else if (track.track_type === TrackType.Subtitle || track.track_type === TrackType.Closedcaption) {
              this.textTracks.push({file, track: {...track}, ...qc})
            } else if (track.track_type === TrackType.Image) {
              this.imageTracks.push({file, track: {...track}, ...qc})
            }
          }
        }
      }
      this.selectedAudioFileTrack = this.audioTracks.find(t => t.track.enabled);
      this.selectedVideoFileTrack = this.videoTracks.find(t => t.track.enabled);
      this.selectedTextFileTrack = this.textTracks.find(t => t.track.enabled);
    })
    combineLatest([this.previewRegistrationService.getPreviews(), this.folderSubject]).pipe(
      map(([ps, folder]) => ps.find(p => p.folder_id === folder?.id)),
      filter(p => p !== undefined),
      mergeMap(preview_data => {
        return this.apollo.mutate<Mutation>({
          mutation: GET_UPFRONT_TOKEN,
          variables: {
            'asset_id': preview_data.asset_id
          }
        }).pipe(
          catchError((err, caught) => {
            this.previewRegistrationService.removePreview(preview_data)
            this.banner.open(err, ['Close']);
            throw err
          })
        )
      }),
      map(upfrontTokenResponse => upfrontTokenResponse.data.previewGenerateToken),
      take(1),
    ).subscribe(upfrontTokenResponse => this.previewTokenPayload = upfrontTokenResponse)
  }

  viewAsListing() {
    this.router.navigate([], {relativeTo: this.route, queryParams: {view: 'listing'}, queryParamsHandling: 'merge', replaceUrl: true})
  }

  generateUploadLink() {
    this.dialog.open(GenerateUploadLinkComponent, {data: {folder: this.folderSubject.value}})
  }

  generatePreview(force = false) {
    const folder = this.folderSubject.value;
    const buttonId = (force ? 'force-preview-' : 'generate-preview-') + folder.id;
    this.pb.setPending(buttonId);
    this.apollo.mutate<Mutation>({
      mutation: PREVIEW_GENERATE,
      variables: {
        folder_id: folder.id,
        force
      }
    }).pipe(
      switchMap(d => this.processService.observeProcess(d.data.previewGenerate, {
        successMessage: `${folder.name} - preview generated.`,
        failureMessage: `${folder.name} - preview failed.`,
        link: window.location.href
      })),
      skipWhile(process => process.state === ProcessStateEnum.InProgress)
    ).subscribe({
      next: process => {
        this.pb.clear(buttonId);
        if (process.state === ProcessStateEnum.Failed) {
          // Drop the cached entry on EVERY failure, deliberately. It survives 12 hours
          // otherwise, so the UI would keep offering a preview the failed run may have
          // invalidated, and the player would fail on every fetch until it aged out.
          //
          // Reviewed and kept as-is: a failure that changes nothing (a lock timeout, a
          // VTK error) leaves the old preview playable, so clearing costs the user a
          // link that still worked. The alternative is for the backend to report
          // whether the preview was actually invalidated and to clear only then.
          // Rejected: a stale entry is replaced by the next successful generate anyway
          // (addPreview replaces by folder_id), so the cost of clearing is one extra
          // click, while the cost of keeping a dead entry is a player that fails on
          // every fetch with no obvious cause. Prefer the recoverable failure.
          this.previewRegistrationService.removePreviewForFolder(folder.id);
          this.banner.open('Preview generation failed. ' + process.message, ['CLOSE'])
        } else if (process.state === ProcessStateEnum.Success) {
          this.previewRegistrationService.addPreview(JSON.parse(process.data));
        }
      },
      error: err => {
        this.pb.clear(buttonId);
        this.previewRegistrationService.removePreviewForFolder(folder.id);
        this.banner.open('Preview generation failed. ' + err, ['CLOSE'])
      }
    })
  }

  addToPo() {
    const preselectedAudio = new Set<string>();
    const preselectedSubtitles = new Set<string>();
    const preselectedCC = new Set<string>();
    const images = new Set<string>();
    for (const file of (this._folder || this.folderSubject.value).files) {
      if (!file.tracks) {
        continue;
      }

      for (const track of file.tracks) {
        if (!track.enabled) {
          continue;
        }

        switch (track.track_type) {
          case TrackType.Audio:
            preselectedAudio.add(track.language);
            break;
          case TrackType.Subtitle:
            preselectedSubtitles.add(track.language);
            break;
          case TrackType.Closedcaption:
            preselectedCC.add(track.language);
            break;
          case TrackType.Image:
            images.add(file.id.split('/').pop());
            continue;
        }
      }
    }

    this.poMetaService.poService.addToPO(
      this.folderSubject.value.id,
      [...preselectedAudio],
      [...preselectedSubtitles],
      [...preselectedCC],
      [...images]
    );
  }

  /** Keys that several tags use: the save would fail, so the page says so and holds it back. */
  get duplicateTagKeys(): string[] {
    return duplicateTagKeys(this.tags);
  }

  openAddTag(): void {
    this.openTagDialog('Add tag', 'Add', {key: '', value: ''}, tag => this.tags = [...this.tags, tag]);
  }

  editTag(index: number): void {
    this.openTagDialog('Edit tag', 'Save', this.tags[index],
      tag => this.tags = this.tags.map((t, i) => i === index ? tag : t));
  }

  removeTag(index: number): void {
    this.tags = this.tags.filter((_, i) => i !== index);
  }

  /** Tags are only changed here; Save changes sends them. */
  private openTagDialog(headline: string, actionName: string, tag: Tag, onConfirm: (tag: Tag) => void): void {
    const data: TagDialogData = {headline, actionName, tag: {key: tag.key, value: tag.value}};
    this.dialog.open<TagDialogResult>(TagDialogComponent, {
      data, role: 'dialog', ariaLabel: headline, autoFocus: 'first-tabbable', restoreFocus: true
    }).closed.subscribe(result => {
      if (result) {
        onConfirm(result);
      }
    });
  }

  save_changes(button: DoButtonComponent) {
    // S3 would refuse the tags after the track updates had gone through, leaving a half-saved page.
    if (this.duplicateTagKeys.length > 0) {
      button.done('Nothing was saved: fix the duplicate tag keys first.', {severity: 'cl-alert-danger'});
      return;
    }
    const saveMutations = []
    for (const f of this._folder.files) {
      const tracks = [];
      // Images are part of the saved track set too — otherwise their "Active" checkbox is a no-op.
      [this.videoTracks, this.audioTracks, this.textTracks, this.imageTracks].forEach(t => {
        t.filter(v => v.file.id === f.id).forEach(v => {
          tracks.push(`{
            index: ${v.track.index},
            enabled: ${v.track.enabled},
            language: "${v.track.language}",
            track_type: ${v.track.track_type}
          }`)
        })
      })
      saveMutations.push(this.apollo.mutate<Mutation>({
        mutation: gql`mutation save_tracks($id: ID!) {
          updateFile(input: {
            file_id: $id
            tracks: [${tracks.join(',\n')}
            ]
          }) {
            file {
              id
              tracks {
                index
                enabled
                language
                track_type
              }
            }
          }
        }
        `,
        variables: {
          id: f.id,
        }
      }))
    }


    const tagInputArray: Array<TagInput> = this.tags.map(({key, value}) =>
      key && key !== '' ? {key, value} : null).filter(p => p !== null);


    saveMutations.push(this.apollo.mutate<Mutation>({
        mutation: gql`
          mutation foldertags($id: ID!, $tags: [TagInput!]) {
            tag_folder(input: { folder_id: $id, tags: $tags } ) {
              folder {
                id
                tags {
                  key
                  value
                }
              }
            }
          }`,
        variables: {
          'id': this.folderSubject.value.id,
          'tags': tagInputArray
        }
      }
    ))
    forkJoin([...saveMutations]).subscribe({
      next: () => button.done('Changes saved successfully'),
      error: err => button.done('Failed to save changes: ' + err)
    })
  }


}
